import { z } from 'zod';

// One-way data migrations (ADR 0019): what the exporter writes, and what an import
// answers. The old database's rows stay loose here: its schema was never written down,
// so the audit reports what it finds, and each import parses only what it takes.

export const BUNDLE_FORMAT = 'itc.fleetcom-export';

/** A row as the old PocketBase returned it. */
const Row = z.record(z.string(), z.unknown());
export type BundleRow = z.infer<typeof Row>;

export const BUNDLE_COLLECTIONS = ['fc_blueprints', 'fc_shipyard_catalog', 'fc_settings', 'fc_shipyards', 'fc_shipyard_pledges', 'fc_parties', 'fc_groups', 'fc_assets', 'members', 'leads', 'threads', 'organizations', 'action_logs', 'commands', 'recruiter_state', 'admin_state', 'onboarding_state'] as const;
export type BundleCollection = (typeof BUNDLE_COLLECTIONS)[number];

/** ITCBot's files on its host, by path under its directory. Only these are ever read. `config/config.json` holds credentials, and must never join them. */
export const HOST_FILES = [
  'recruiter/recruiter_payouts.json',
  'recruiter/recruiter_credits.json',
  'recruiter/lead_contributors.json',
  'recruiter/recruiter_config.json',
  'recruiter/harvest.txt',
  'recruiter/leads.txt',
  'recruiter/failed.txt',
  'config/sync_history.json',
  'reports/reports_stats.json',
  'reports/reports_config.json',
  'executivehangar/executivehangar_calibration.json',
  'ping/ping_config.json',
  'autorole/autorole_config.json',
  'events/events_config.json',
  'onboarding/onboarding_config.json',
] as const;

/** The bundle's version, which moves whenever `BUNDLE_COLLECTIONS` or the shape does: an older bundle would fail to parse on a missing key. */
export const BUNDLE_VERSION = 3;

/** An export of a restored backup. It holds member names, so it never goes in git. */
export const FleetcomBundle = z.object({
  format: z.literal(BUNDLE_FORMAT),
  version: z.literal(BUNDLE_VERSION),
  exportedAt: z.iso.datetime(),
  /** Each collection's field names, as the old instance reports them. */
  schema: z.record(z.string(), z.array(z.string())),
  collections: z.object(Object.fromEntries(BUNDLE_COLLECTIONS.map((name) => [name, z.array(Row)])) as Record<BundleCollection, z.ZodArray<typeof Row>>),
  /** ITCBot's host files, or null if the export didn't read them. A `.json` holds its parsed value, a `.txt` its text. */
  hostFiles: z.object({ read: z.record(z.string(), z.unknown()), missing: z.array(z.string()) }).nullable(),
});
export type FleetcomBundle = z.infer<typeof FleetcomBundle>;

export const NOT_A_BUNDLE = 'That isn’t an export from pnpm fleetcom:export';

/** An export that can't be read, with the reason in words an admin can act on. */
export class BundleError extends Error {
  override name = 'BundleError';
}

/** Why a bundle of another version can't be used, or null if `json` isn't a bundle of any version or is this one. A bundle never had a path forward from an older one (ADR 0019). */
export function bundleVersionProblem(json: unknown): string | null {
  if (typeof json !== 'object' || json === null) return null;
  const { format, version } = json as { format?: unknown; version?: unknown };
  if (format !== BUNDLE_FORMAT || version === BUNDLE_VERSION) return null;
  return `This export is version ${typeof version === 'number' ? String(version) : 'unknown'}; this import needs version ${String(BUNDLE_VERSION)}: export it again with pnpm fleetcom:export`;
}

/** `FleetcomBundle.parse`, with a plain message for a bundle of the wrong version or no bundle at all, in place of a validation dump. */
export function parseBundle(json: unknown): FleetcomBundle {
  const wrongVersion = bundleVersionProblem(json);
  if (wrongVersion !== null) throw new BundleError(wrongVersion);
  const parsed = FleetcomBundle.safeParse(json);
  if (!parsed.success) throw new BundleError(NOT_A_BUNDLE);
  return parsed.data;
}

/** What an importer shows when reading its file failed: the bundle's own reason if it has one, else the same line as for any file that isn't an export. */
export const bundleProblem = (error: unknown): string => (error instanceof BundleError ? error.message : NOT_A_BUNDLE);

/** Rows per import request: one unit of work, with room for its audit entry. */
export const IMPORT_SLICE = 80;

/** What an import did, or a dry run would do. */
export const ImportReport = z.object({
  dryRun: z.boolean(),
  created: z.number().int(),
  skipped: z.number().int(),
  refused: z.array(z.object({ id: z.string(), reason: z.string() })),
});
export type ImportReport = z.infer<typeof ImportReport>;
